import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DiscountCodeContext from "../../../context/discount-code/context";
import DiscountCodeContextProvider from "../../../context/discount-code/provider";
import { IUpdateDiscountCode } from "../../../models/discount-code/request";
import { getTranslations } from "../../../utils/helpers/translations";
import { execute } from "../../../utils/helpers/execute";
import moment from "moment";
import { useForm, useWatch } from "react-hook-form";
import { FormWithSteps } from "./FormWithSteps";
import { OrderLimitType } from "../../../models/discount-code/enum";

const Form = () => {
    const navigate = useNavigate();
    const { details, actions } = useContext(DiscountCodeContext);
    const { id } = useParams();
    const methods = useForm<any>({
        defaultValues: {
            userType: details?.userType ?? undefined,
            code: details?.code,
            codeValidity: details?.codeValidity,
            discountValueProduct: details?.discountValueProduct,
            discountValueDelivery: details?.discountValueDelivery,
            maxOfUse: details?.maxOfUse,
            imageId: details?.image?.id ?? undefined,
            notifyImageId: details?.notifyImage?.id ?? undefined,
            note: getTranslations(details?.translations?.note),
            numberPoints: details?.numberPoints,
            status: details?.status ?? undefined,
            type: details?.type ?? undefined,
            validityDate: details?.validityDate ? moment(details?.validityDate).format("YYYY-MM-DD") : undefined,
            name: getTranslations(details?.translations?.name),
            showInApp: details?.showInApp ?? false,
            canBePurchased: details?.canBePurchased ?? false,
            typeApp: details?.typeApp,
            isInvitation: details?.isInvitation,
            companyId: details?.companyId,
            specialStoreId: details?.specialStoreId,
            sponsorType: details?.sponsorType ?? 1,
            typeValue: details?.typeValue ?? undefined,
            minNumberOfOrders: details?.minNumberOfOrders ?? undefined,
            maxNumberOfOrders: details?.maxNumberOfOrders ?? undefined,
            minDaysSinceLastCompletedOrder: details?.minDaysSinceLastCompletedOrder ?? undefined,
            maxDaysSinceLastCompletedOrder: details?.maxDaysSinceLastCompletedOrder ?? undefined,
            message: details?.message ?? undefined,
            minAge: details?.minAge ?? undefined,
            maxAge: details?.maxAge ?? undefined,
            tagIds: details?.tags?.map((tag) => tag?.id),

            enableAgeRange: !!details?.minAge || !!details?.maxAge,

            gender: details?.gender ?? undefined,
            enableGender: !!details?.gender || false,

            areaId: details?.area?.id ?? undefined,
            enableArea: !!details?.area || false,

            enableNumberOfOrders: !!details?.minNumberOfOrders || false,
            orderLimitType: details?.minNumberOfOrders && details?.maxNumberOfOrders && details?.minNumberOfOrders == details?.maxNumberOfOrders ? OrderLimitType.EXACT
                : details?.minNumberOfOrders && details?.maxNumberOfOrders ? OrderLimitType.RANGE
                    : details?.minNumberOfOrders ? OrderLimitType.MIN
                        : details?.maxNumberOfOrders ? OrderLimitType.MAX
                            : undefined,
            exactNumberOfOrders: details?.minNumberOfOrders == details?.maxNumberOfOrders ? details?.maxNumberOfOrders : undefined,

            enableDaysSinceLastCompletedOrder: !!details?.minDaysSinceLastCompletedOrder || false,
            daysLimitType: details?.minDaysSinceLastCompletedOrder && details?.maxDaysSinceLastCompletedOrder && details?.minDaysSinceLastCompletedOrder == details?.maxDaysSinceLastCompletedOrder ? OrderLimitType.EXACT
                : details?.minDaysSinceLastCompletedOrder && details?.maxDaysSinceLastCompletedOrder ? OrderLimitType.RANGE
                    : details?.minDaysSinceLastCompletedOrder ? OrderLimitType.MIN
                        : details?.maxDaysSinceLastCompletedOrder ? OrderLimitType.MAX
                            : undefined,
            exactDaysSinceLastCompletedOrder: details?.minDaysSinceLastCompletedOrder == details?.maxDaysSinceLastCompletedOrder ? details?.maxDaysSinceLastCompletedOrder : undefined,
        } as IUpdateDiscountCode,
    });

    const {
        reset,
        control,
        setValue,
    } = methods;

    useEffect(() => {
        const getDetails = async () => {
            id && (await actions.getDetails(Number(id)));
        };
        getDetails();
    }, []);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const onSubmit = async (data: any) => {
        await execute({
            callback: async () => {
                console.log(data)
                data.numberOfUseAllowed = data.numberOfUseAllowed * data.maxOfUse;
                if (data.isInvitation) {
                    data.numberPoints = null;
                    data.validityDate = null;
                }
                if (data.exactNumberOfOrders) {
                    data.maxNumberOfOrders = exactNumberOfOrders
                    data.minNumberOfOrders = exactNumberOfOrders
                }
                if (data.exactDaysSinceLastCompletedOrder) {
                    data.maxDaysSinceLastCompletedOrder = exactDaysSinceLastCompletedOrder
                    data.minDaysSinceLastCompletedOrder = exactDaysSinceLastCompletedOrder
                }
                if (data.type == 1) {
                    data.numberPoints = null;
                }
                data.discountValueProduct =
                    typeApp == 1 ? undefined : data.discountValueProduct;
                data.discountValueDelivery =
                    typeApp == 2 ? undefined : data.discountValueDelivery;
                setLoadingSubmit(true);
                console.log(data)
                details
                    ? await actions.updateDiscountCode(details?.id, data)
                    : await actions.createDiscountCode(data);
                navigate(-1);
            },
            fallback: () => {
            },
                   finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    const byInvitation = useWatch({
        control,
        name: "isInvitation",
    });
    const type = useWatch({
        control,
        name: "type",
    });
    const userType = useWatch({
        control,
        name: "userType",
    });
    const exactNumberOfOrders = useWatch({
        control,
        name: "exactNumberOfOrders",
    });

    const exactDaysSinceLastCompletedOrder = useWatch({
        control,
        name: "exactDaysSinceLastCompletedOrder",
    });

    useEffect(() => {
        if (type === 2) {
            setValue("canBePurchased", true);
            setValue("userType", 1);
        } else {
            if (!byInvitation) {
                setValue("userType", undefined);
            }
            setValue("canBePurchased", false);
        }
    }, [type]);

    useEffect(() => {
        if (byInvitation || type !== 1 || userType === 3) {
            setValue("enableAgeRange", false);
            setValue("minAge", undefined);
            setValue("maxAge", undefined);
            setValue("enableGender", false);
            setValue("gender", undefined);
            setValue("enableArea", false);
            setValue("areaId", undefined);
            setValue("enableNumberOfOrders", false);
            setValue("enableDaysSinceLastCompletedOrder", false);
            setValue("minNumberOfOrders", undefined);
        }
        if (byInvitation || type !== 2) {
            setValue("showInApp", false);
        }
    }, [byInvitation, type, userType, setValue]);

    useEffect(() => {
        if (byInvitation) {
            setValue("userType", 3);
            setValue("type", 1);
            setValue("typeValue", 1);
            setValue("showInApp", false);
            setValue("canBePurchased", false);
            setValue("sponsorType", 1);
        }
        else {
            setValue("userType", undefined);
            setValue("type", undefined);
            setValue("typeValue", undefined);
            setValue("showInApp", false);
            setValue("canBePurchased", false);
            setValue("sponsorType", undefined);
        }
    }, [byInvitation]);

    const typeApp = useWatch({
        control,
        name: "typeApp",
    });

    const enableGender = useWatch({
        control,
        name: "enableGender",
    });

    const enableAgeRange = useWatch({
        control,
        name: "enableAgeRange",
    });

    const enableNumberOfOrders = useWatch({
        control,
        name: "enableNumberOfOrders",
    });

    const enableDaysSinceLastCompletedOrder = useWatch({
        control,
        name: "enableDaysSinceLastCompletedOrder",
    });

    const enableArea = useWatch({
        control,
        name: "enableArea",
    });

    const orderLimitType = useWatch({
        control,
        name: "orderLimitType",
    });

    const daysLimitType = useWatch({
        control,
        name: "daysLimitType",
    });

    useEffect(() => {
        setValue("exactNumberOfOrders", undefined);
        setValue("minNumberOfOrders", undefined);
        setValue("maxNumberOfOrders", undefined);
    }, [orderLimitType]);

    useEffect(() => {
        setValue("exactDaysSinceLastCompletedOrder", undefined);
        setValue("minDaysSinceLastCompletedOrder", undefined);
        setValue("maxDaysSinceLastCompletedOrder", undefined);
    }, [daysLimitType]);

    useEffect(() => {
        reset({
            userType: details?.userType,
            code: details?.code,
            codeValidity: details?.codeValidity,
            discountValueProduct: details?.discountValueProduct,
            discountValueDelivery: details?.discountValueDelivery,
            maxOfUse: details?.maxOfUse,
            imageId: details?.image?.id ?? undefined,
            notifyImageId: details?.notifyImage?.id ?? undefined,
            note: getTranslations(details?.translations?.note),
            numberPoints: details?.numberPoints,
            status: details?.status,
            type: details?.type,
            validityDate: details?.validityDate ? moment(details?.validityDate).format("YYYY-MM-DD") : undefined,
            name: getTranslations(details?.translations?.name),
            showInApp: details?.showInApp ?? false,
            canBePurchased: details?.canBePurchased ?? false,
            typeApp: details?.typeApp,
            isInvitation: details?.isInvitation,
            companyId: details?.companyId,
            specialStoreId: details?.specialStoreId,
            sponsorType: details?.sponsorType ?? 1,
            typeValue: details?.typeValue,
            minNumberOfOrders: details?.minNumberOfOrders ?? undefined,
            maxNumberOfOrders: details?.maxNumberOfOrders ?? undefined,
            minDaysSinceLastCompletedOrder: details?.minDaysSinceLastCompletedOrder ?? undefined,
            maxDaysSinceLastCompletedOrder: details?.maxDaysSinceLastCompletedOrder ?? undefined,
            message: details?.message ?? undefined,
            minAge: details?.minAge ?? undefined,
            maxAge: details?.maxAge ?? undefined,
            tagIds: details?.tags?.map((tag) => tag?.id),
            enableAgeRange: !!details?.minAge || !!details?.maxAge,

            gender: details?.gender ?? undefined,
            enableGender: !!details?.gender || false,

            areaId: details?.area?.id ?? undefined,
            enableArea: !!details?.area || false,

            enableNumberOfOrders: !!details?.minNumberOfOrders || false,
            orderLimitType: details?.minNumberOfOrders && details?.maxNumberOfOrders && details?.minNumberOfOrders == details?.maxNumberOfOrders ? OrderLimitType.EXACT
                : details?.minNumberOfOrders && details?.maxNumberOfOrders ? OrderLimitType.RANGE
                    : details?.minNumberOfOrders ? OrderLimitType.MIN
                        : details?.maxNumberOfOrders ? OrderLimitType.MAX
                            : undefined,

            exactNumberOfOrders: details?.minNumberOfOrders == details?.maxNumberOfOrders ? details?.maxNumberOfOrders : undefined,

            enableDaysSinceLastCompletedOrder: !!details?.minDaysSinceLastCompletedOrder || false,
            daysLimitType: details?.minDaysSinceLastCompletedOrder && details?.maxDaysSinceLastCompletedOrder && details?.minDaysSinceLastCompletedOrder == details?.maxDaysSinceLastCompletedOrder ? OrderLimitType.EXACT
                : details?.minDaysSinceLastCompletedOrder && details?.maxDaysSinceLastCompletedOrder ? OrderLimitType.RANGE
                    : details?.minDaysSinceLastCompletedOrder ? OrderLimitType.MIN
                        : details?.maxDaysSinceLastCompletedOrder ? OrderLimitType.MAX
                            : undefined,

            exactDaysSinceLastCompletedOrder: details?.minDaysSinceLastCompletedOrder == details?.maxDaysSinceLastCompletedOrder ? details?.maxDaysSinceLastCompletedOrder : undefined,
        } as IUpdateDiscountCode);
    }, [details]);

    return (
        <FormWithSteps methods={methods} onSubmit={onSubmit} enableUserConditions={enableGender || enableAgeRange || enableArea || enableNumberOfOrders || enableDaysSinceLastCompletedOrder} />
    );
};
const DiscountCodeForm = () => {
    return (
        <DiscountCodeContextProvider>
            <Form />
        </DiscountCodeContextProvider>
    );
};
export default DiscountCodeForm;
